import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { billingOrgInvitesQueryKey } from '@/hooks/billing/types'
import { useHandleError } from '@/hooks/common/useErrorHandler'

import { UUID } from '@/types/Id'

interface TargetOrganizationInput {
  organization_id: string
}

interface OrganizationInviteCreateInput {
  invitee_email: string
  target_organizations: TargetOrganizationInput[]
  billing_role: string
}

interface InviteToOrganizationVariables {
  billingOrgId: UUID
  inviteData: OrganizationInviteCreateInput
}

const inviteToOrganization = async (
  billingOrgId: UUID,
  inviteData: OrganizationInviteCreateInput,
  accessToken: string | undefined | null,
): Promise<void> => {
  if (!accessToken) {
    throw new Error('No access token provided.')
  }
  if (!billingOrgId) {
    throw new Error('Billing Organization ID is required.')
  }

  const url = `${API_URL}/api/v1/invites/${billingOrgId}`

  const res = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(inviteData),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}))
    throw new Error(
      errorData.detail || `Failed to send invitation: ${res.statusText}`,
    )
  }
}

export const useInviteToOrganization = () => {
  const { accessToken } = useAuthInfo()
  const { handleError } = useHandleError()
  const queryClient = useQueryClient()

  return useMutation<void, Error, InviteToOrganizationVariables>({
    mutationFn: (variables: InviteToOrganizationVariables) =>
      inviteToOrganization(
        variables.billingOrgId,
        variables.inviteData,
        accessToken,
      ),
    onError: (err: Error, variables: InviteToOrganizationVariables) => {
      handleError(err, false, 'Failed to send invitation.')
      console.error(
        `Error inviting user: ${variables.inviteData.invitee_email} to org: ${variables.billingOrgId}`,
        err,
      )
    },
    onSuccess: (_, variables) => {
      toast.success('Invitation sent successfully!')

      // Invalidate the invites query to refresh the list of pending invites
      return queryClient.invalidateQueries({
        queryKey: billingOrgInvitesQueryKey.all(variables.billingOrgId),
      })
    },
  })
}
